import React, { useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { PunchSeverity } from '../types';
import { getProjectPunchItems, createPunchItem, nextItemNumber } from '../services/punchService';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from './BoardModal';

const SEVERITY_LABEL: Record<number, string> = {
  [PunchSeverity.Minor]: 'Minor',
  [PunchSeverity.Major]: 'Major',
  [PunchSeverity.Critical]: 'Critical',
};
const SEVERITY_ORDER = [PunchSeverity.Critical, PunchSeverity.Major, PunchSeverity.Minor];

/**
 * Quick-create punch item modal — reused by the board card "Create Punch Item"
 * action. Self-contained: fetches current items to compute the next sequential
 * item number, pre-fills the title from the task, and POSTs without navigating
 * away from the board. Lives in components/ (not the Punch tab) so the board
 * bundle doesn't pull in the PDF/export dependencies.
 */
export default function QuickCreatePunchModal({ pca, projectId, initialName, onClose, onCreated }: {
  pca: IPublicClientApplication; projectId: string; initialName: string;
  onClose: () => void; onCreated: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [location, setLocation] = useState('');
  const [trade, setTrade] = useState('');
  const [severity, setSeverity] = useState<number>(PunchSeverity.Minor);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      const existing = await getProjectPunchItems(pca, projectId);
      await createPunchItem(pca, {
        projectId,
        name: name.trim(),
        location: location.trim() || null,
        trade: trade.trim() || null,
        severity,
        itemnumber: nextItemNumber(existing),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Create Punch Item" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>
          {saving ? 'Creating…' : 'Create Punch Item'}
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Title *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} style={BM_TITLE_INPUT} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Location</label>
          <input value={location} onChange={e => setLocation(e.target.value)} style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>Trade</label>
          <input value={trade} onChange={e => setTrade(e.target.value)} style={BM_INPUT} />
        </div>
      </div>
      <div style={{ marginBottom: 4 }}>
        <label style={BM_LABEL}>Severity</label>
        <select value={severity} onChange={e => setSeverity(Number(e.target.value))} style={BM_INPUT}>
          {SEVERITY_ORDER.map(v => <option key={v} value={v}>{SEVERITY_LABEL[v]}</option>)}
        </select>
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}
